import '../../styles/restScreens.css';
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';

// BL-177 Step 2o (7b): the one place on a client's card for their client-app (portal) access.
//   Status:   Not invited · Invited (date) · Accepted · Setup complete · Removed
//   Actions:  Invite · Resend · Revoke (as before)  ·  Remove portal access (accepted or finished)  ·  Re-invite (after a removal)
// Everyone on the team sees the status; only an owner / admin / the account's owner is offered the buttons (the server checks again).
// Removing access signs the client out and unlinks the client app from this contact; their login and their trips are not touched.
// Every action goes through the portal-invite function, which sends only the contact id (never an email address).

const MIN_REASON = 3;
const MAX_REASON = 500;

const when = (value) => (value ? new Date(value).toLocaleDateString() : '');

export function describeAccess(access) {
    if (!access) return { label: '', detail: '' };
    switch (access.state) {
    case 'invited':
        return {
            label: `Invited ${when(access.invited_at)}`.trim(),
            detail: access.expired ? 'The link has expired. Resend to send a new one.' : `The link expires ${when(access.expires_at)}.`,
        };
    case 'accepted':
        return { label: 'Accepted', detail: `${access.accepted_at ? `Accepted ${when(access.accepted_at)}. ` : ''}They have not finished the first-time setup yet.` };
    case 'setup_complete':
        return { label: 'Setup complete', detail: 'They can use the client app.' };
    case 'removed':
        return {
            label: 'Removed',
            detail: `${access.removed_by_name ? `Removed by ${access.removed_by_name}` : 'Removed'}${access.removed_at ? ` on ${when(access.removed_at)}` : ''}${access.removed_reason ? `. Reason: ${access.removed_reason}` : ''}.`,
        };
    default:
        return { label: 'Not invited', detail: 'They do not have the client app yet.' };
    }
}

export default function ClientPortalAccessPanel({ contactId, contactName = '' }) {
    const [access, setAccess] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [removing, setRemoving] = useState(false);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        const { data, error: readError } = await supabaseClient.rpc('bl382_portal_access_status', { p_contact_id: contactId });
        if (readError) throw readError;
        setAccess(data || null);
    }, [contactId]);
    useEffect(() => {
        load().catch((loadError) => {
            console.error('Unable to load client portal access:', loadError);
            setError('Client portal access could not be loaded.');
        });
    }, [load]);

    async function run(action, extra = {}) {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            const { data, error: invokeError } = await supabaseClient.functions.invoke('portal-invite', {
                body: { contactId, action, ...extra },
            });
            if (invokeError) {
                const details = invokeError.context instanceof Response ? await invokeError.context.json() : null;
                throw new Error(details?.error || invokeError.message);
            }
            if (data?.error) throw new Error(data.error);
            if (action === 'revoke') setMessage('Invitation revoked.');
            else if (action === 'remove') {
                setMessage(`Portal access removed${contactName ? ` for ${contactName}` : ''}.${data?.signedOutSessions > 0 ? ' They were signed out.' : ''} Their login and trips were not changed.`);
            } else setMessage('Invitation sent. The link expires in seven days.');
            await load();
            return true;
        } catch (actionError) {
            setError(actionError.message || 'That did not work.');
            return false;
        } finally {
            setBusy(false);
        }
    }

    async function confirmRemove() {
        const ok = await run('remove', { reason: reason.trim() });
        if (ok) { setRemoving(false); setReason(''); }
    }

    const state = access?.state;
    const { label, detail } = describeAccess(access);
    const canManage = access?.can_manage === true;
    const reasonLength = reason.trim().length;
    const reasonOk = reasonLength >= MIN_REASON && reasonLength <= MAX_REASON;
    const noEmail = access && access.has_valid_email === false;

    return (
        <section data-m6-layout="clients" data-m6-component="ClientPortalAccessPanel" aria-label="Client portal access" data-testid="client-portal-access" className="mt-3 rounded-lg border border-[var(--surface-border)] p-3"
                 onDoubleClick={(event) => event.stopPropagation()}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Client portal access</h3>
            {access && (
                <p className="mt-1 text-sm text-[var(--ink)]" data-testid="portal-access-status">
                    <span className="font-semibold">{label}</span>
                    {detail && <span className="text-[var(--ink-muted)]"> — {detail}</span>}
                </p>
            )}
            {canManage && (
                <SupportWriteGuard>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {state === 'not_invited' && (
                            <Button3D variant="pill" disabled={busy || noEmail} onClick={() => run('invite')}>{busy ? 'Working…' : 'Invite to portal'}</Button3D>
                        )}
                        {state === 'invited' && (
                            <>
                                <Button3D variant="pill" disabled={busy} onClick={() => run('resend')}>{busy ? 'Working…' : 'Resend invitation'}</Button3D>
                                <Button3D variant="pill" disabled={busy} onClick={() => run('revoke')}>Revoke invitation</Button3D>
                            </>
                        )}
                        {(state === 'accepted' || state === 'setup_complete') && (
                            <Button3D variant="pill" disabled={busy} onClick={() => { setRemoving(true); setError(''); setMessage(''); }}>Remove portal access</Button3D>
                        )}
                        {state === 'removed' && (
                            <Button3D variant="pill" disabled={busy || noEmail} onClick={() => run('invite')}>{busy ? 'Working…' : 'Re-invite to portal'}</Button3D>
                        )}
                    </div>
                    {noEmail && (state === 'not_invited' || state === 'removed') && (
                        <p className="mt-1 text-xs text-[var(--ink-muted)]">Save a valid email on this contact first; the invitation goes to the email on file.</p>
                    )}
                </SupportWriteGuard>
            )}
            {access && !canManage && (
                <p className="mt-1 text-xs text-[var(--ink-muted)]">Only an owner, an admin or the account owner can change portal access.</p>
            )}
            {message && <p role="status" className="mt-1 text-xs text-[var(--ink-muted)]">{message}</p>}
            {error && !removing && <p role="alert" className="mt-1 text-xs text-[var(--ui-alert)]">{error}</p>}
            {removing && (
                <Modal3D
                    portal
                    stripe="red"
                    size="small"
                    testId="remove-portal-access"
                    title="Remove portal access?"
                    subtitle={`${contactName || 'This client'} will lose the client app right away and be signed out.`}
                    onClose={() => { if (!busy) { setRemoving(false); setReason(''); setError(''); } }}
                    actions={(
                        <>
                            <Button3D variant="light" disabled={busy} onClick={() => { setRemoving(false); setReason(''); setError(''); }}>Cancel</Button3D>
                            <Button3D variant="primary" disabled={busy || !reasonOk} onClick={confirmRemove}>{busy ? 'Removing…' : 'Remove access'}</Button3D>
                        </>
                    )}
                >
                    <p className="text-sm">Their login and their trips are not deleted. You can invite them again any time.</p>
                    <label className="mt-3 block text-sm font-semibold" htmlFor={`portal-remove-reason-${contactId}`}>Why? (required)</label>
                    <textarea
                        id={`portal-remove-reason-${contactId}`}
                        className="mt-1 w-full rounded border border-[var(--surface-border)] bg-transparent p-2 text-sm"
                        rows={3}
                        maxLength={MAX_REASON}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="For example: test client finished, or the client asked us to"
                    />
                    {error && <p role="alert" className="mt-1 text-xs text-[var(--ui-alert)]">{error}</p>}
                </Modal3D>
            )}
        </section>
    );
}
